<template>
    <div class="text-input formcontrol" :class="containerClasses">
        <label>{{ label }}</label>

        <input
            type="text"
            name="text-field"
            :autocomplete="props.allowAutocomplete ? 'on' : 'off'"
            v-model="inputValue"
            @keyup="onInputKeyUp"
            @focus="onInputFocus"
            @blur="onInputBlur"
            :disabled="props.disabled"
            ref="textInputElement"
        />

        <!-- Validation Icon -->
        <div v-if="isIconVisible" class="icon" :class="{ active: isIconActive }">
            <i class="fas fa-check"></i>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { InputRequirementType } from "@/components/elements/InputRequirementType";
    import { translations } from "@/shared/localization/translations";
    import { computed, ref, watch } from "vue";

    // Props
    const props = withDefaults(
        defineProps<{
            requirementType?: InputRequirementType;
            label?: string;
            indent?: boolean;
            disabled?: boolean;
            value?: string;
            verified?: boolean;
            allowAutocomplete?: boolean;
            displayIcon?: boolean;
            maxCharLength?: number;
        }>(),
        {
            verified: undefined,
            disabled: false,
            allowAutocomplete: false,
            displayIcon: true,
        }
    );

    // Emits
    const emit = defineEmits(["update:value", "submit", "onStartEditing", "onEndEditing"]);

    // State
    const inputValue = ref(props.value);
    const isEditing = ref(false);
    const isTextValid = ref<boolean | undefined>(undefined);
    const isVerified = ref<boolean | undefined>(props.verified);
    const showError = ref(false);
    const textInputElement = ref<HTMLElement | undefined>();
    const requirementType = ref<InputRequirementType | undefined>(props.requirementType);
    const label = computed<string | undefined>(() => {
        if (props.label == null || props.label!.length == 0) {
            return undefined;
        }

        if (requirementType.value === InputRequirementType.Optional) {
            return props.label + " (" + translations.addToCartPopover.optional + ")";
        }

        return props.label;
    });

    // Computed
    const isEdited = computed(() => inputValue.value !== undefined && inputValue.value.length > 0);
    const hasVerificationResult = computed(() => isVerified.value !== undefined);
    const isIconVisible = computed(() => props.displayIcon && !hasVerificationResult.value);
    const isIconActive = computed(() => isTextValid.value === true);

    const containerClasses = computed(() => ({
        intent: props.indent,
        editing: isEditing.value,
        edited: isEdited.value,
        "has-error": showError.value,
        hasconfirmation: hasVerificationResult.value,
        disabled: props.disabled,
    }));

    // Methods
    function isValid(): boolean {
        if (requirementType.value === InputRequirementType.Required) {
            const currentInput = inputValue.value;

            if (currentInput == null || currentInput.length == 0) {
                return false;
            }

            if (props.maxCharLength != null && currentInput.length > props.maxCharLength) {
                return false;
            }
        }

        return true;
    }

    function setValue(text: string) {
        inputValue.value = text;
    }

    function getValue() {
        return isEdited.value ? inputValue.value : undefined;
    }

    function setVerified(ver?: boolean) {
        isVerified.value = ver;
    }

    function setError(error: boolean) {
        showError.value = error;
    }

    function focus() {
        if (textInputElement.value) {
            $(textInputElement.value).focus();
        }
    }

    // Event Handlers
    function onInputKeyUp(e: KeyboardEvent) {
        emit("update:value", inputValue.value);

        if (isValid() && e.key === "Enter") {
            emit("submit");
        }
    }

    function onInputFocus() {
        isEditing.value = true;
    }

    function onInputBlur() {
        isEditing.value = false;
    }

    // function checkEmailInput(removeWhiteSpace?: boolean) {
    //     if (inputValue.value && removeWhiteSpace) {
    //         inputValue.value = inputValue.value.replace(/\s+/g, "");
    //     }
    //
    //     if (isVerified.value !== undefined) return;
    //
    //     isTextValid.value = isValid();
    //
    //     if (isTextValid.value) {
    //         showError.value = false;
    //     }
    // }

    // Watchers
    watch(
        () => props.value,
        (newValue) => {
            inputValue.value = newValue;
        }
    );

    watch(
        () => props.verified,
        (newValue) => {
            isVerified.value = newValue;
        }
    );

    watch(inputValue, () => {
        emit("update:value", inputValue.value);
    });

    watch(isEditing, (newValue) => {
        if (newValue) emit("onStartEditing");
        else emit("onEndEditing");
    });

    // Exposed API
    defineExpose({
        isValid,
        setValue,
        getValue,
        setError,
        setVerified,
        focus,
    });
</script>
